import {
  forwardRef,
  memo,
  type ReactNode,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type DragEvent,
  JSX,
} from "react";
import DashboardWidgetContent from "src/components/builder/DashboardWidgetContent";
import useBuilderStore, {
  type BuilderCanvasItem,
} from "src/store/builderStore";
import builderWidgetDataByIdDummy from "src/config/dummy/builderWidgetDataByIdDummy.json";

type BuilderWidgetDataEntry = {
  data?: unknown;
  [key: string]: unknown;
};

type BuilderWidgetDataByIdDummy = {
  widgets?: Record<string, BuilderWidgetDataEntry>;
};

const BUILDER_WIDGET_DATA_BY_ID =
  (builderWidgetDataByIdDummy as BuilderWidgetDataByIdDummy).widgets ?? {};

const resolveBuilderWidgetData = (widgetTemplateId: string): unknown => {
  if (!widgetTemplateId) return null;

  const widgetPayload = BUILDER_WIDGET_DATA_BY_ID[widgetTemplateId];

  if (!widgetPayload || typeof widgetPayload !== "object") return null;

  if (!Object.prototype.hasOwnProperty.call(widgetPayload, "data")) {
    return widgetPayload;
  }

  const payload = widgetPayload as {
    data?: unknown;
    metadata?: Record<string, unknown>;
    chart_type?: string;
  };

  if (payload.chart_type === "tiered_bar") {
    return {
      records: Array.isArray(payload.data)
        ? payload.data
        : (payload.data as any)?.records ?? [],
      tier_rules: payload.metadata?.tier_rules ?? {},
    };
  }

  if (payload.chart_type === "special_quadrant_chart") {
    return {
      practices: Array.isArray(payload.data) ? payload.data : [],
      kpi: (payload as any).kpi ?? {},
      tier_rules: payload.metadata?.tier_rules ?? {},
    };
  }

  // These widgets read metadata (totals, limits) alongside the rows.
  if (
    payload.chart_type === "donut_pie" ||
    payload.chart_type === "custom_lines" ||
    payload.chart_type === "custom_table" ||
    payload.chart_type === "deviation_bar" ||
    payload.chart_type === "average_bar" ||
    payload.chart_type === "cards" ||
    payload.chart_type === "bar_drilldown"
  ) {
    return { data: payload.data ?? [], metadata: payload.metadata ?? {} };
  }

  return payload.data ?? null;
};

type SlotProps = {
  item: BuilderCanvasItem;
  isInteractionActive?: boolean;
  isRecentlyAdded?: boolean;
  isDropTarget: boolean;
  onDragOver: (event: DragEvent<HTMLElement>, itemId: string) => void;
  onDrop: (event: DragEvent<HTMLElement>, itemId: string) => void;
  onDragLeave: (itemId: string) => void;
  onRemove?: (itemId: string) => void;
  isWidgetFormInitiallyOpen?: boolean;
  onWidgetFormSaved?: (itemId: string) => void;
  children?: ReactNode;
} & Omit<
  ComponentPropsWithoutRef<"div">,
  "onDragOver" | "onDrop" | "onDragLeave"
>;

const Slot = forwardRef<HTMLDivElement, SlotProps>(function Slot(
  {
    item,
    isInteractionActive = false,
    isRecentlyAdded = false,
    isDropTarget,
    isWidgetFormInitiallyOpen = false,
    onWidgetFormSaved,
    onDragOver,
    onDrop,
    onDragLeave,
    onRemove,
    children,
    className,
    style,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    ...rest
  },
  ref,
): JSX.Element {
  const storedItem = useBuilderStore(
    (state) =>
      state.canvasItems.find((canvasItem) => canvasItem.i === item.i) ?? item,
  );
  const assignedWidget = useBuilderStore(
    (state) =>
      state.dashboardTemplates.find(
        (widget) => widget.id === storedItem.assignedWidgetId,
      ) ?? null,
  );
  const selectedDashboardCode = useBuilderStore(
    (state) => state.selectedDashboardCode,
  );
  const queueWidgetDeletion = useBuilderStore(
    (state) => state.queueWidgetDeletion,
  );

  const assignedWidgetCode = assignedWidget
    ? String(assignedWidget.code ?? "")
    : "";

  const resolvedWidgetData = assignedWidget
    ? resolveBuilderWidgetData(assignedWidgetCode)
    : null;

  const hasAssignment = !!storedItem.assignedWidgetId;

  const handleRemove = () => {
    if (
      selectedDashboardCode &&
      storedItem.isServerWidgetAssignment &&
      storedItem.persistedWidgetId
    ) {
      queueWidgetDeletion(storedItem.persistedWidgetId);
    }

    onRemove?.(storedItem.i);
  };

  return (
    <div
      {...rest}
      ref={ref}
      className={`builder-live-grid__item${className ? ` ${className}` : ""}`}
      style={{ ...style } as CSSProperties}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
    >
      <article
        className={[
          "builder-canvas-card",
          isDropTarget && "is-drop-target",
          (assignedWidget || hasAssignment) && "has-widget",
          isRecentlyAdded && "is-recently-added",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label={`${storedItem.label} slot`}
        onDragOver={(event) => onDragOver(event, storedItem.i)}
        onDrop={(event) => onDrop(event, storedItem.i)}
        onDragLeave={() => onDragLeave(storedItem.i)}
      >
        <div className="builder-canvas-card__header">
          <div className="builder-canvas-card__header-group">
            <span className="builder-canvas-card__badge">
              {assignedWidget?.title ?? (hasAssignment ? storedItem.label : "Empty Slot")}
            </span>
            {!assignedWidget && !hasAssignment && (
              <span className="builder-canvas-card__dimensions">
                {storedItem.w} x {storedItem.h} • min {storedItem.minW} x{" "}
                {storedItem.minH}
              </span>
            )}
          </div>

          {onRemove && (assignedWidget || hasAssignment) && (
            <div className="builder-canvas-card__controls">
              <button
                type="button"
                id="remove-widget-button"
                className="builder-canvas-card__action"
                aria-label={`Remove ${storedItem.label}`}
                onClick={handleRemove}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          )}
        </div>

        {assignedWidget ? (
          <div className="builder-canvas-card__body">
            {isInteractionActive ? (
              <div className="builder-canvas-card__preview">
                <span className="placeholder builder-canvas-card__placeholder builder-canvas-card__placeholder--block" />
                <div className="builder-canvas-card__preview-copy">
                  <strong>{assignedWidget.title}</strong>
                  <span>
                    {storedItem.w} x {storedItem.h} • moving preview
                  </span>
                </div>
              </div>
            ) : (
              <div
                className={[
                  "builder-canvas-card__content",
                  ["data_table", "map"].includes(
                    assignedWidget?.chartType ?? "",
                  ) && "builder-canvas-card__content--scrollable",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <DashboardWidgetContent
                  key={`${storedItem.i}-${storedItem.w}-${storedItem.h}`}
                  chartType={assignedWidget.chartType}
                  title={assignedWidget.title}
                  data={resolvedWidgetData}
                  isLoading={false}
                  hasError={false}
                  isBuilderPreview={true}
                />
              </div>
            )}
          </div>
        ) : hasAssignment ? (
          <div className="builder-canvas-card__body">
            <div className="placeholder-glow h-100">
              <span className="placeholder w-100 h-100" />
            </div>
          </div>
        ) : (
          <div className="builder-canvas-card__body">
            <div className="builder-canvas-card__dropzone">
              <span
                className="placeholder builder-canvas-card__placeholder builder-canvas-card__placeholder--block"
                aria-hidden="true"
              />
              <div className="builder-canvas-card__dropzone-copy">
                <strong>Add a widget here</strong>
                <span>
                  Click from the sidebar or drag a widget into this slot.
                </span>
              </div>
            </div>
          </div>
        )}
      </article>
      {children}
    </div>
  );
});

export default memo(Slot);
